import React, { useState, useEffect } from 'react';
import { LoaderCircle } from 'lucide-react';
import { useAuth } from 'oidc-react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';

import Project from '../../common/Utils/project';
import { decodeBase64 } from '../../common/Utils/base64';
import { downloadCSV } from '../../common/Utils/downloadHelpers';

import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { useAgentHeartBeatService } from '../../components/services/agentHeartBeatService';
import { useSoftwareInventory } from '../../components/services/softwareInventoryService';

import api from '../../components/services/apiService';
import { useToast } from '../../providers/ToastProvider/ToastContext';

import { AgentStatusIndicator } from '../../components/AgentStatus';
import { ColumnConfig } from '../../components/Table';

import DynamicDataTooltip from '../../components/DynamicDataTooltip/DynamicDataTooltip';

import { Asset, BannerCard, SoftwareSummary, OverrideMode, VersionGroupedRow, GroupGroupedRow, ITEMS_PER_PAGE } from './infoTableTypes';
import {
  checkSoftwarePolicies,
  submitSoftwarePolicies,
  PolicyMode,
  SoftwarePolicy,
} from '../../components/services/softwareComplianceService';
import InfoView from './InfoView';
import SoftwareInfoView from './SoftwareInfoView';
import SoftwarePolicyModal from '../../components/Modal/SoftwarePolicyModal';

// Large enough to retrieve every matching asset in a single request for client-side pagination.
const FETCH_ALL_PAGE_SIZE = 9999;

// ================= COMPONENT =================
const SoftwareInfoTable = () => {
  type TenantGroup = {
    tenantGroupId: string;
    groupName: string;
    isActive: boolean;
    isDefault: boolean;
  };

  // ================= STATE =================
  const [softwareAssets, setSoftwareAssets] = useState<Asset[]>([]);
  const [softwareInfo, setSoftwareInfo] = useState<SoftwareSummary>({
    softwareAssetName: '',
    softwareAssetVersion: '',
    latestPackageVersion: '',
    packageIdentifier: '',
    currentPackageVersionDate: '',
    latestPackageVersionDate: '',
    agentCount: 0,
  });

  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);

  const [loading, setLoading] = useState(true);
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [showPatchModal, setShowPatchModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [allSelected, setAllSelected] = useState(false);
  const [viewMode, setViewMode] = useState<'device' | 'version' | 'group'>('device');
  const [selectedVersionKeys, setSelectedVersionKeys] = useState<Set<string>>(new Set());
  const [selectedGroupKeys, setSelectedGroupKeys] = useState<Set<string>>(new Set());
  const [selectedPolicyAction, setSelectedPolicyAction] = useState<OverrideMode | ''>('');
  const [submittingVersionPolicy, setSubmittingVersionPolicy] = useState(false);
  const [softwarePolicyMode, setSoftwarePolicyMode] = useState<PolicyMode | null>(null);
  const [softwarePolicy, setSoftwarePolicy] = useState<SoftwarePolicy | null>(null);
  const [tenantGroups, setTenantGroups] = useState<TenantGroup[]>([]);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [selectAllGroups, setSelectAllGroups] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());

  // ================= HOOKS =================
  const auth = useAuth();
  const { type, id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedTenantId } = useTenantSelection();
  const rowDetails = location.state;
  const { addToast } = useToast();
  const { getAgentHeartbeatStatus } = useAgentHeartBeatService();
  const { getSoftwareAssets, getSoftwareAssetsBySoftwareName, getSoftwareSummary, getSoftwareInventorySummary } = useSoftwareInventory();

  // ================= HELPERS =================
  const getSquareStyle = (isActive: boolean) => ({
    width: '20px',
    height: '20px',
    borderRadius: '4px',
    border: isActive ? '#00FF00' : '5px solid #444',
    backgroundColor: isActive ? '#00FF00' : 'transparent',
    cursor: 'pointer',
    display: 'inline-block',
  });

  const queryParams = new URLSearchParams(location.search);
  const rawQueryViewMode = queryParams.get('view');
  const queryViewMode: 'device' | 'version' | 'group' =
    rawQueryViewMode === 'version' || rawQueryViewMode === 'group' ? rawQueryViewMode : 'device';
  const normalizedBase64Id = (id || '').replace(/ /g, '+');

  // ================= DERIVED =================
  const bannerCards: BannerCard[] = [];
  if (softwareInfo) {
    if (softwareInfo.softwareAssetVersion) {
      bannerCards.push({
        label: 'Current Version',
        value: softwareInfo.softwareAssetVersion,
        date: softwareInfo.currentPackageVersionDate,
      });
    }

    if (softwareInfo.packageIdentifier) {
      bannerCards.push({
        label: 'Latest Version',
        value: softwareInfo.latestPackageVersion,
        date: softwareInfo.latestPackageVersionDate,
      });
    }

    if (softwareInfo.availableVersionCount != null && type === 'software-info') {
      bannerCards.push({
        label: 'Available Versions Count',
        value: softwareInfo.availableVersionCount,
      });
    }

    bannerCards.push({ label: 'Total Devices', value: softwareInfo.agentCount });
    bannerCards.push({
      label: 'Supported',
      value: softwareInfo.packageIdentifier ? 'Yes' : 'No',
    });
  }

  const bannerColumns = softwareInfo ? Math.min(Math.max(bannerCards.length, 1), 5) : 4;

  const tableData = showActiveOnly ? softwareAssets.filter((item) => item.isAgentActive) : softwareAssets;
  // Frontend pagination over the full fetched set — keeps Version/Group view aggregation
  // (which reduces over tableData) accurate across every device, not just one backend page.
  const pagedTableData = tableData.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const selectedRow = softwareInfo
    ? {
        patchName: {
          name: softwareInfo.softwareAssetName,
          image: '',
          packageIdentifier: softwareInfo.packageIdentifier,
        },
        remediated: '',
        deviceImpactedTotal: '',
        agentList: selectedItemIds,
        type: 'thirdParty' as const,
        osUpdateId: undefined,
        osUpdateType: undefined,
        packageIdentifier: softwareInfo.packageIdentifier,
        packageVersion: softwareInfo.softwareAssetVersion,
        cveScore: queryParams.get('cveScore') ?? undefined,
        urlFilter: undefined,
        latestVersionKey: softwareInfo.latestPackageVersion,
        selectedDevices: selectedItemIds.length.toString(),
      }
    : null;

  const totalPages = Math.max(1, Math.ceil(tableData.length / ITEMS_PER_PAGE));
  const isSoftwareInfo = type === 'software-info';
  const baseIsBlackListed = Boolean((rowDetails as { isBlackListed?: boolean } | null)?.isBlackListed);

  const viewOptions = [
    { val: 'device', label: 'Device View' },
    { val: 'version', label: 'Version View' },
    { val: 'group', label: 'Group View' },
  ] as const;
  const activeViewIndex = viewOptions.findIndex((o) => o.val === viewMode);

  const columnWidths = isSoftwareInfo
    ? {
        select: '8%',
        agentName: '20%',
        softwareAssetVersion: '12%',
        osType: '10%',
        groupName: '20%',
        installedDate: '10%',
        status: '5%',
      }
    : {
        select: '8%',
        agentName: '26%',
        groupName: '30%',
        installedDate: '14%',
        status: '12%',
      };

  // ================= API =================
  const getAssetsStatuses = async (agentIds: string[]) => {
    try {
      const data = await getAgentHeartbeatStatus(selectedTenantId!, agentIds);
      return data?.isSuccess ? data.data : [];
    } catch {
      return [];
    }
  };

  const getBannerData = async (softwareAssetInventoryId: string) => {
    try {
      const data =
        type === 'software-info'
          ? await getSoftwareInventorySummary(selectedTenantId!, decodeBase64(softwareAssetInventoryId.replace(/ /g, '+')))
          : await getSoftwareSummary(selectedTenantId!, softwareAssetInventoryId);

      if (data) setSoftwareInfo(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTenantGroups = async () => {
    try {
      if (!auth?.userData || !selectedTenantId) return;
      const response = await api.get(`${Project.api}tenantservice/TenantGroup/${selectedTenantId}/groups?includeInactive=true`);
      const data = response?.data;
      if (data?.isSuccess && Array.isArray(data?.data)) {
        const groups = data.data[0]?.tenantGroupList || [];
        const normalizedGroups = groups
          .filter((g: { isActive?: boolean }) => g.isActive)
          .map((g: { tenantGroupId?: string; tenantGroupID?: string; groupName?: string; isActive?: boolean; isDefault?: boolean }) => ({
            tenantGroupId: g.tenantGroupId || g.tenantGroupID || '',
            groupName: g.groupName || '',
            isActive: Boolean(g.isActive),
            isDefault: Boolean(g.isDefault),
          }))
          .filter((g: TenantGroup) => Boolean(g.tenantGroupId));

        setTenantGroups(normalizedGroups);
      }
    } catch (err) {
      console.error(err);
      setTenantGroups([]);
    }
  };

  // Fetches every matching asset in one call (large pageSize) so pagination — and the
  // Version/Group view aggregation, which reduces over the full set — can run entirely
  // on the client instead of only seeing whichever single backend page was loaded.
  const fetchSoftwareAssets = async () => {
    try {
      setLoading(true);

      if (!selectedTenantId || !id) return;

      const responseData =
        type === 'software-info'
          ? await getSoftwareAssetsBySoftwareName(selectedTenantId, decodeBase64(normalizedBase64Id), 1, FETCH_ALL_PAGE_SIZE, searchTerm)
          : await getSoftwareAssets(selectedTenantId, id, 1, FETCH_ALL_PAGE_SIZE, searchTerm);

      const data = responseData?.data;
      if (!data?.assetByAgentDetails) {
        setSoftwareAssets([]);
        return;
      }

      const agentIds = data.assetByAgentDetails.map((a: Asset) => a.tenantAgentID);
      const heartbeat = await getAssetsStatuses(agentIds);

      const merged = data.assetByAgentDetails.map((asset: Asset) => {
        const hb = heartbeat.find((h: any) => h.tenantAgentId === asset.tenantAgentID);
        return {
          ...asset,
          agentName: hb?.hostName || asset.agentName,
          isAgentActive: hb?.agentStatus === 'Active',
          statusMessage: hb?.heartbeatStatusMessage || '',
        };
      });

      setSoftwareAssets(merged);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const checkSoftwarePolicy = async (softwareName: string) => {
    try {
      if (!selectedTenantId || !softwareName) {
        setSoftwarePolicy(null);
        setSoftwarePolicyMode(baseIsBlackListed ? PolicyMode.Blacklist : null);
        return;
      }

      const policies = await checkSoftwarePolicies(selectedTenantId, [softwareName]);
      const policy = policies[0];
      setSoftwarePolicy(policy || null);
      setSoftwarePolicyMode(policy ? policy.policyMode : null);
    } catch (err) {
      console.error(err);
      setSoftwarePolicy(null);
      setSoftwarePolicyMode(baseIsBlackListed ? PolicyMode.Blacklist : null);
    }
  };

  // ================= EFFECTS =================
  useEffect(() => {
    if (auth?.userData) {
      fetchSoftwareAssets();
      getBannerData(id!);
    }
    setCurrentPage(1);
  }, [auth, searchTerm]);

  useEffect(() => {
    fetchTenantGroups();
  }, [selectedTenantId]);

  useEffect(() => {
    const softwareName = softwareInfo?.softwareAssetName || (isSoftwareInfo ? decodeBase64(normalizedBase64Id) : '');
    checkSoftwarePolicy(softwareName);
  }, [softwareInfo?.softwareAssetName, isSoftwareInfo, id, selectedTenantId, baseIsBlackListed]);

  useEffect(() => {
    const ids = pagedTableData.map((i) => i.tenantAgentID);
    setAllSelected(ids.length > 0 && ids.every((id) => selectedItemIds.includes(id)));
  }, [pagedTableData, selectedItemIds]);

  useEffect(() => {
    if (!isSoftwareInfo) {
      if (viewMode !== 'device') {
        setViewMode('device');
      }
      return;
    }

    if (viewMode !== queryViewMode) {
      setViewMode(queryViewMode);
    }
  }, [isSoftwareInfo, queryViewMode, viewMode]);

  // ================= ACTIONS =================
  const handleAllSelect = () => {
    const ids = pagedTableData.map((i) => i.tenantAgentID);

    setSelectedItemIds((prev) => (allSelected ? prev.filter((id) => !ids.includes(id)) : Array.from(new Set([...prev, ...ids]))));
  };

  const handleToggle = (id: string) => {
    setSelectedItemIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleActiveFilter = () => {
    setShowActiveOnly((s) => !s);
  };

  const handlePageChange = (page: number) => {
    if (page !== currentPage) setCurrentPage(page);
  };

  const handleOpenRemediateModal = () => {
    if (!softwareInfo) return;
    setShowPatchModal(true);
  };

  const handleViewModeChange = (mode: 'device' | 'version' | 'group') => {
    setViewMode(mode);
    // Each view paginates its own (differently-sized) dataset, so a page number
    // carried over from another view can point past the end of this one.
    setCurrentPage(1);

    const params = new URLSearchParams(location.search);
    params.set('view', mode);
    const query = params.toString();
    navigate(query ? `${location.pathname}?${query}` : location.pathname, { replace: true });

    if (mode === 'version' || mode === 'group') {
      setSelectedItemIds([]);
      setAllSelected(false);
      if (mode === 'version') {
        setSelectedGroupKeys(new Set());
      }
      if (mode === 'group') {
        setSelectedVersionKeys(new Set());
      }
    } else {
      setSelectedVersionKeys(new Set());
      setSelectedGroupKeys(new Set());
    }
  };

  const versionViewData: VersionGroupedRow[] = Object.values(
    tableData.reduce(
      (acc, item) => {
        const version = item.softwareAssetVersion || '-';
        if (!acc[version]) {
          acc[version] = {
            softwareAssetVersion: version,
            endpointCount: 0,
            serverCount: 0,
          };
        }
        const osType = (item.osType || '').toLowerCase();
        if (osType.includes('client')) {
          acc[version].endpointCount += 1;
        } else {
          acc[version].serverCount += 1;
        }
        return acc;
      },
      {} as Record<string, VersionGroupedRow>,
    ),
  );

  const groupViewData: GroupGroupedRow[] = Object.values(
    tableData.reduce(
      (acc, item) => {
        const tenantGroupId =
          item.tenantGroupId || tenantGroups.find((group) => group.groupName === item.groupName)?.tenantGroupId || 'ungrouped';
        const groupName = item.groupName || 'Ungrouped';

        if (!acc[tenantGroupId]) {
          acc[tenantGroupId] = {
            tenantGroupId,
            groupName,
            endpointCount: 0,
            serverCount: 0,
          };
        }

        const osType = (item.osType || '').toLowerCase();
        if (osType.includes('client')) {
          acc[tenantGroupId].endpointCount += 1;
        } else {
          acc[tenantGroupId].serverCount += 1;
        }

        return acc;
      },
      {} as Record<string, GroupGroupedRow>,
    ),
  );

  // Version/Group aggregation reduces over the full fetched set (above), but pagination
  // and totalPages must reflect the size of whichever view is actually on screen —
  // otherwise a page count based on the device list bleeds into these much smaller views.
  const activeViewDataLength =
    viewMode === 'version' ? versionViewData.length : viewMode === 'group' ? groupViewData.length : tableData.length;
  const activeViewTotalPages = Math.max(1, Math.ceil(activeViewDataLength / ITEMS_PER_PAGE));
  const pagedVersionViewData = versionViewData.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const pagedGroupViewData = groupViewData.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const selectableGroupRows = groupViewData.filter((row) => row.tenantGroupId !== 'ungrouped');

  const normalizedBlockedVersions = (softwarePolicy?.blockedVersions || []).filter((v) => v && v !== '-');
  const normalizedGroups = softwarePolicy?.groupIdList || softwarePolicy?.groups || [];

  const updateVersionList = (mode: OverrideMode, selectedVersions: string[]) => {
    const next = new Set(normalizedBlockedVersions);
    if (mode === 'Blacklist') {
      selectedVersions.forEach((v) => next.add(v));
    } else {
      selectedVersions.forEach((v) => next.delete(v));
    }
    return Array.from(next);
  };

  const updateGroupList = (mode: OverrideMode, selectedGroupIds: string[]) => {
    const next = new Set(normalizedGroups);
    if (mode === 'Blacklist') {
      selectedGroupIds.forEach((g) => next.add(g));
    } else {
      selectedGroupIds.forEach((g) => next.delete(g));
    }
    return Array.from(next);
  };

  const allVersionsSelected =
    versionViewData.length > 0 && versionViewData.every((row) => selectedVersionKeys.has(row.softwareAssetVersion));

  const handleSelectAllVersions = () => {
    if (allVersionsSelected) {
      setSelectedVersionKeys(new Set());
      return;
    }
    setSelectedVersionKeys(new Set(versionViewData.map((row) => row.softwareAssetVersion)));
  };

  const handleToggleVersion = (version: string) => {
    const next = new Set(selectedVersionKeys);
    if (next.has(version)) {
      next.delete(version);
    } else {
      next.add(version);
    }
    setSelectedVersionKeys(next);
  };

  const allGroupsSelected = selectableGroupRows.length > 0 && selectableGroupRows.every((row) => selectedGroupKeys.has(row.tenantGroupId));

  const handleSelectAllGroupsView = () => {
    if (allGroupsSelected) {
      setSelectedGroupKeys(new Set());
      return;
    }
    setSelectedGroupKeys(new Set(selectableGroupRows.map((row) => row.tenantGroupId)));
  };

  const handleToggleGroup = (groupId: string) => {
    if (groupId === 'ungrouped') {
      return;
    }

    const next = new Set(selectedGroupKeys);
    if (next.has(groupId)) {
      next.delete(groupId);
    } else {
      next.add(groupId);
    }
    setSelectedGroupKeys(next);
  };

  const handlePolicySubmit = async (overrideGroupsOrEvent?: string[] | React.SyntheticEvent) => {
    if (!selectedTenantId || !selectedPolicyAction) return;

    const overrideGroups = Array.isArray(overrideGroupsOrEvent) ? overrideGroupsOrEvent : undefined;

    const isVersionView = viewMode === 'version';
    const isGroupView = viewMode === 'group';
    const versionSelections = Array.from(selectedVersionKeys).filter((v) => v && v !== '-');
    const groupSelections = Array.from(selectedGroupKeys).filter((groupId) => groupId && groupId !== 'ungrouped');

    if (isVersionView && selectedVersionKeys.size === 0) return;
    if (isGroupView && groupSelections.length === 0) return;

    if (isVersionView && selectedPolicyAction === 'Blacklist' && normalizedGroups.length === 0 && !overrideGroups) {
      setSelectedGroups(new Set());
      setSelectAllGroups(false);
      setShowGroupModal(true);
      return;
    }

    const softwareName = softwareInfo?.softwareAssetName || (isSoftwareInfo ? decodeBase64(normalizedBase64Id) : '');
    const blockedVersions = isVersionView ? updateVersionList(selectedPolicyAction, versionSelections) : normalizedBlockedVersions;
    const groupIdList = overrideGroups || (isGroupView ? updateGroupList(selectedPolicyAction, groupSelections) : normalizedGroups);
    const isBlacklistEmpty = groupIdList.length === 0 && blockedVersions.length === 0;
    const updatedByClaim = auth?.userData?.profile?.name;
    const updatedBy = typeof updatedByClaim === 'string' ? updatedByClaim : '';
    const nextPolicyMode: PolicyMode =
      selectedPolicyAction === 'Blacklist' ? PolicyMode.Blacklist : isBlacklistEmpty ? PolicyMode.Allowlist : PolicyMode.Blacklist;

    try {
      setSubmittingVersionPolicy(true);

      const result = await submitSoftwarePolicies(selectedTenantId, [
        {
          packageIdentifier: softwareInfo?.packageIdentifier || '',
          softwareName,
          policyMode: nextPolicyMode,
          groupIdList,
          updatedBy,
          blockedVersions,
        },
      ]);

      if (result?.isSuccess) {
        addToast(result.message || 'Policy updated successfully', 'success');
        setSelectedPolicyAction('');
        setSelectedVersionKeys(new Set());
        setSelectedGroupKeys(new Set());
        setShowGroupModal(false);
        await checkSoftwarePolicy(softwareName);
      } else {
        addToast('Failed to update policy', 'error');
      }
    } catch (err) {
      console.error(err);
      addToast('Failed to update policy', 'error');
    } finally {
      setSubmittingVersionPolicy(false);
    }
  };

  // ================= TABLE =================
  const columns: ColumnConfig[] = [
    {
      key: 'select',
      header: '',
      width: columnWidths.select,
      renderHeader: () => <div onClick={handleAllSelect} style={getSquareStyle(allSelected)} />,
      render: (_value: unknown, item: Asset) => (
        <div onClick={() => handleToggle(item.tenantAgentID)} style={getSquareStyle(selectedItemIds.includes(item.tenantAgentID))} />
      ),
    },
    {
      key: 'agentName',
      header: 'Device Name',
      width: columnWidths.agentName,
      render: (value: string, item: Asset) => (
        <DynamicDataTooltip
          data={item}
          excludeFields={[
            'tenantAgentID',
            'agentName',
            'softwareAssetVersion',
            'osType',
            'groupName',
            'installedDate',
            'statusMessage',
            'isAgentActive',
          ]}
        >
          <span>{value}</span>
        </DynamicDataTooltip>
      ),
    },
    ...(isSoftwareInfo
      ? [
          {
            key: 'softwareAssetVersion',
            header: 'Installed Version',
            width: columnWidths.softwareAssetVersion,
            render: (value: string, item: Asset) => (
              <DynamicDataTooltip
                data={item}
                excludeFields={[
                  'tenantAgentID',
                  'agentName',
                  'softwareAssetVersion',
                  'osType',
                  'groupName',
                  'installedDate',
                  'statusMessage',
                  'isAgentActive',
                ]}
              >
                <span>{value || '-'}</span>
              </DynamicDataTooltip>
            ),
          },
          {
            key: 'osType',
            header: 'OS Type',
            width: columnWidths.osType,
            render: (value: string, item: Asset) => (
              <DynamicDataTooltip
                data={item}
                excludeFields={[
                  'tenantAgentID',
                  'agentName',
                  'softwareAssetVersion',
                  'osType',
                  'groupName',
                  'installedDate',
                  'statusMessage',
                  'isAgentActive',
                ]}
              >
                <span>{value || '-'}</span>
              </DynamicDataTooltip>
            ),
          },
        ]
      : []),
    {
      key: 'groupName',
      header: 'Group',
      width: columnWidths.groupName,
      render: (value: string, item: Asset) => (
        <DynamicDataTooltip
          data={item}
          excludeFields={[
            'tenantAgentID',
            'agentName',
            'softwareAssetVersion',
            'osType',
            'groupName',
            'installedDate',
            'statusMessage',
            'isAgentActive',
          ]}
        >
          <span>{value || '-'}</span>
        </DynamicDataTooltip>
      ),
    },
    {
      key: 'installedDate',
      header: 'Install Date',
      width: columnWidths.installedDate,
      render: (value: string, item: Asset) => (
        <DynamicDataTooltip
          data={item}
          excludeFields={[
            'tenantAgentID',
            'agentName',
            'softwareAssetVersion',
            'osType',
            'groupName',
            'installedDate',
            'statusMessage',
            'isAgentActive',
          ]}
        >
          <span>{value && !value.startsWith('0001-01-01') ? moment(value).format('DD MMM YYYY') : '-'}</span>
        </DynamicDataTooltip>
      ),
    },
    {
      key: 'status',
      header: <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>Status</div>,
      width: columnWidths.status,
      render: (_value: unknown, item: Asset) => (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <AgentStatusIndicator
            agentId={item.tenantAgentID}
            isActive={item.isAgentActive}
            statusMessage={item.statusMessage}
            isLoading={item.statusMessage === undefined}
          />
        </div>
      ),
    },
  ];

  const versionColumns: ColumnConfig[] = [
    {
      key: 'versionSelect',
      header: '',
      width: '8%',
      align: 'center',
      renderHeader: () => <div onClick={handleSelectAllVersions} style={getSquareStyle(allVersionsSelected)} />,
      render: (_value: unknown, item: VersionGroupedRow) => (
        <div
          onClick={() => handleToggleVersion(item.softwareAssetVersion)}
          style={getSquareStyle(selectedVersionKeys.has(item.softwareAssetVersion))}
        />
      ),
    },
    {
      key: 'softwareAssetVersion',
      header: 'Installed Version',
      width: '24%',
      renderHeader: () => <span style={{ display: 'block', width: '100%', paddingLeft: 8 }}>Installed Version</span>,
      render: (value: string) => <span style={{ paddingLeft: 8 }}>{value || '-'}</span>,
    },
    {
      key: 'endpointCount',
      header: 'Installed Endpoints',
      width: '18%',
      align: 'center',
      renderHeader: () => <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>Installed Endpoints</span>,
      render: (value: number) => <span>{value}</span>,
    },
    {
      key: 'serverCount',
      header: 'Installed Servers',
      width: '18%',
      align: 'center',
      renderHeader: () => <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>Installed Servers</span>,
      render: (value: number) => <span>{value}</span>,
    },
    {
      key: 'appLock',
      header: 'APP LOCK',
      width: '32%',
      align: 'center',
      renderHeader: () => <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>APP LOCK</span>,
      render: (_value: unknown, item: VersionGroupedRow) => {
        if (normalizedBlockedVersions.length === 0 || !item.softwareAssetVersion || item.softwareAssetVersion === '-') {
          return <span className="text-gray-400">-</span>;
        }

        const isBlocked = normalizedBlockedVersions.includes(item.softwareAssetVersion);

        return (
          <div className="flex justify-center items-center">
            <button
              className={`inline-flex min-w-[130px] items-center justify-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] ${
                isBlocked ? 'border-[#D12B2B] text-[#FF8A8A] bg-[#2A0B0B]' : 'border-[#00C16A] text-[#38FFB3] bg-[#06281A]'
              }`}
            >
              {isBlocked ? 'BLOCKED' : 'ALLOWED'}
            </button>
          </div>
        );
      },
    },
  ];

  const groupColumns: ColumnConfig[] = [
    {
      key: 'groupSelect',
      header: '',
      width: '8%',
      align: 'center',
      renderHeader: () => <div onClick={handleSelectAllGroupsView} style={getSquareStyle(allGroupsSelected)} />,
      render: (_value: unknown, item: GroupGroupedRow) => {
        const isSelectable = item.tenantGroupId !== 'ungrouped';

        return (
          <div
            onClick={() => handleToggleGroup(item.tenantGroupId)}
            style={{
              ...getSquareStyle(selectedGroupKeys.has(item.tenantGroupId)),
              cursor: isSelectable ? 'pointer' : 'not-allowed',
              opacity: isSelectable ? 1 : 0.4,
            }}
          />
        );
      },
    },
    {
      key: 'groupName',
      header: 'Group Name',
      width: '24%',
      renderHeader: () => <span style={{ display: 'block', width: '100%', paddingLeft: 8 }}>Group Name</span>,
      render: (value: string) => <span style={{ paddingLeft: 8 }}>{value || '-'}</span>,
    },
    {
      key: 'endpointCount',
      header: 'Installed Endpoints',
      width: '18%',
      align: 'center',
      renderHeader: () => <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>Installed Endpoints</span>,
      render: (value: number) => <span>{value}</span>,
    },
    {
      key: 'serverCount',
      header: 'Installed Servers',
      width: '18%',
      align: 'center',
      renderHeader: () => <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>Installed Servers</span>,
      render: (value: number) => <span>{value}</span>,
    },
    {
      key: 'appLock',
      header: 'APP LOCK',
      width: '32%',
      align: 'center',
      renderHeader: () => <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>APP LOCK</span>,
      render: (_value: unknown, item: GroupGroupedRow) => {
        if (normalizedGroups.length === 0) {
          return <span className="text-gray-400">-</span>;
        }

        const isBlocked = normalizedGroups.includes(item.tenantGroupId);

        return (
          <div className="flex justify-center items-center">
            <button
              className={`inline-flex min-w-[130px] items-center justify-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] ${
                isBlocked ? 'border-[#D12B2B] text-[#FF8A8A] bg-[#2A0B0B]' : 'border-[#00C16A] text-[#38FFB3] bg-[#06281A]'
              }`}
            >
              {isBlocked ? 'BLOCKED' : 'ALLOWED'}
            </button>
          </div>
        );
      },
    },
  ];

  // ================= LOADING =================
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        <LoaderCircle className="animate-spin w-10 h-10 text-white" />
      </div>
    );
  }

  // Exports every device row for this software (softwareAssets holds the full, unpaginated
  // result), not just the page currently shown in the table.
  const handleDownloadCSV = () => {
    if (softwareAssets.length === 0) {
      addToast('No data to export.', 'info', 4000);
      return;
    }

    const rows = softwareAssets.map((asset) => ({
      agentName: asset.agentName,
      groupName: asset.groupName ?? '',
      softwareAssetVersion: asset.softwareAssetVersion ?? '',
      osName: asset.osName ?? '',
      osVersion: asset.osVersion ?? '',
      osType: asset.osType ?? '',
      installationScope: asset.installationScope ?? '',
      installedDate: asset.installedDate ?? '',
      lastUpdated: asset.lastUpdated ?? '',
      agentStatus: asset.isAgentActive ? 'Active' : 'Inactive',
    }));

    const softwareName = (softwareInfo?.softwareAssetName || 'software').replace(/[^a-z0-9._-]+/gi, '_');
    downloadCSV(rows, `${softwareName}_devices_${new Date().toISOString().slice(0, 10)}`, {
      agentName: 'Device',
      groupName: 'Group',
      softwareAssetVersion: 'Software Version',
      osName: 'OS',
      osVersion: 'OS Version',
      osType: 'OS Type',
      installationScope: 'Installation Scope',
      installedDate: 'Installed Date',
      lastUpdated: 'Last Updated',
      agentStatus: 'Agent Status',
    });
  };

  // ================= SHARED PROPS =================
  const sharedProps = {
    softwareInfo,
    bannerCards,
    bannerColumns,
    hasSoftwarePolicy: softwarePolicyMode !== null,
    softwarePolicyMode,
    tableData: pagedTableData,
    columns,
    loading,
    showActiveOnly,
    showPatchModal,
    selectedRow,
    currentPage,
    totalPages,
    selectedItemIds,
    onActiveFilter: handleActiveFilter,
    onOpenRemediateModal: handleOpenRemediateModal,
    onPageChange: handlePageChange,
    onCloseModal: () => setShowPatchModal(false),
    onDownloadCSV: handleDownloadCSV,
  };

  // ================= DISPATCHER =================
  if (isSoftwareInfo) {
    return (
      <>
        <SoftwareInfoView
          {...sharedProps}
          totalPages={activeViewTotalPages}
          versionColumns={versionColumns}
          groupColumns={groupColumns}
          versionViewData={pagedVersionViewData}
          groupViewData={pagedGroupViewData}
          viewMode={viewMode}
          viewOptions={viewOptions}
          activeViewIndex={activeViewIndex}
          selectedVersionKeys={selectedVersionKeys}
          selectedGroupKeys={selectedGroupKeys}
          selectedPolicyAction={selectedPolicyAction}
          submittingVersionPolicy={submittingVersionPolicy}
          onViewModeChange={handleViewModeChange}
          onSelectedPolicyActionChange={setSelectedPolicyAction}
          onSubmitVersionPolicy={handlePolicySubmit}
        />

        <SoftwarePolicyModal
          show={showGroupModal}
          policyMode={PolicyMode.Blacklist}
          selectedItemCount={selectedVersionKeys.size}
          selectAllGroups={selectAllGroups}
          tenantGroups={tenantGroups}
          selectedGroups={selectedGroups}
          submittingPolicy={submittingVersionPolicy}
          onClose={() => setShowGroupModal(false)}
          onSubmit={() => handlePolicySubmit(Array.from(selectedGroups))}
          onSelectAllGroupsChange={(checked) => {
            setSelectAllGroups(checked);
            if (checked) {
              setSelectedGroups(new Set(tenantGroups.map((g) => g.tenantGroupId)));
            } else {
              setSelectedGroups(new Set());
            }
          }}
          onGroupToggle={(groupId, checked) => {
            const next = new Set(selectedGroups);
            if (checked) {
              next.add(groupId);
            } else {
              next.delete(groupId);
            }

            if (next.size !== tenantGroups.length && selectAllGroups) {
              setSelectAllGroups(false);
            }

            if (tenantGroups.length > 0 && next.size === tenantGroups.length && !selectAllGroups) {
              setSelectAllGroups(true);
            }

            setSelectedGroups(next);
          }}
        />
      </>
    );
  }

  return <InfoView {...sharedProps} rowDetails={rowDetails} />;
};

export default SoftwareInfoTable;
